/**
 * Panel compound component.
 *
 * ADR-022 Pattern 1: Compound Components with context.
 *
 * Usage:
 *   <Panel defaultCollapsed={false}>
 *     <Panel.Header>Title</Panel.Header>
 *     <Panel.Content>Body content here</Panel.Content>
 *   </Panel>
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

/* ------------------------------------------------------------------ */
/*  Context                                                           */
/* ------------------------------------------------------------------ */

interface PanelContextValue {
  isCollapsed: boolean;
  toggle: () => void;
}

const PanelContext = createContext<PanelContextValue | null>(null);

function usePanelContext(): PanelContextValue {
  const ctx = useContext(PanelContext);
  if (!ctx) {
    throw new Error('Panel.Header / Panel.Content must be used within <Panel>');
  }
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

function PanelHeader({ children, subtitle }: { children: ReactNode; subtitle?: ReactNode }) {
  const { isCollapsed, toggle } = usePanelContext();

  return (
    <button
      type="button"
      className="panel-header"
      onClick={toggle}
      aria-expanded={!isCollapsed}
    >
      <span className="panel-header__title">
        {children}
        {isCollapsed && subtitle && (
          <span className="panel-header__subtitle">{subtitle}</span>
        )}
      </span>
      <span
        className={`panel-header__chevron ${isCollapsed ? 'panel-header__chevron--collapsed' : ''}`}
        aria-hidden="true"
      >
        &#9662;
      </span>
    </button>
  );
}

function PanelContent({ children }: { children: ReactNode }) {
  const { isCollapsed } = usePanelContext();
  if (isCollapsed) return null;

  return <div className="panel-content">{children}</div>;
}

/* ------------------------------------------------------------------ */
/*  Root                                                              */
/* ------------------------------------------------------------------ */

interface PanelRootProps {
  children: ReactNode;
  defaultCollapsed?: boolean;
}

function PanelRoot({ children, defaultCollapsed = false }: PanelRootProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const toggle = useCallback(() => setIsCollapsed((prev) => !prev), []);

  return (
    <PanelContext.Provider value={{ isCollapsed, toggle }}>
      <section className="panel" data-collapsed={isCollapsed}>
        {children}
      </section>
    </PanelContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Namespace export                                                  */
/* ------------------------------------------------------------------ */

export const Panel = Object.assign(PanelRoot, {
  Header: PanelHeader,
  Content: PanelContent,
});
